import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';
import { translateUiText } from './translateUiText';
import { isRtlLanguage } from './RtlCacheProvider';
import type { RootState } from '../store';

type TranslationMap = Record<string, string>;

export interface LangMeta {
  code: string;
  label: string;
  nativeLabel: string;
  dir: 'ltr' | 'rtl';
  flag: string;
  isDefault?: boolean;
}

const FALLBACK_LANGUAGES: LangMeta[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr', flag: '🇬🇧', isDefault: true },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl', flag: '🇸🇦' },
];

const FLAG_BY_CODE: Record<string, string> = {
  en: '🇬🇧', hi: '🇮🇳', ta: '🇮🇳', te: '🇮🇳', kn: '🇮🇳', ml: '🇮🇳', mr: '🇮🇳',
  bn: '🇧🇩', gu: '🇮🇳', pa: '🇮🇳', ur: '🇵🇰', ar: '🇸🇦', he: '🇮🇱', fa: '🇮🇷',
  fr: '🇫🇷', de: '🇩🇪', es: '🇪🇸', pt: '🇧🇷', it: '🇮🇹', zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷',
};

const LANG_STORAGE_KEY = 'appLanguage';

function tenantLangKey(slug: string) {
  return `appLanguage:${slug || 'default'}`;
}

function tenantLangUserKey(slug: string) {
  return `appLanguageUserSet:${slug || 'default'}`;
}

function currentTenantSlug() {
  return (localStorage.getItem('tenantSlug') || '').trim().toLowerCase();
}

function persistActiveLanguage(code: string, slug: string, userChosen: boolean) {
  localStorage.setItem(LANG_STORAGE_KEY, code);
  if (slug) localStorage.setItem(tenantLangKey(slug), code);
  if (userChosen) {
    localStorage.setItem(tenantLangUserKey(slug), '1');
  } else if (slug) {
    localStorage.removeItem(tenantLangUserKey(slug));
  }
}

function tenantDefaultLanguage(rows: LangMeta[]): string {
  return rows.find((r) => r.isDefault)?.code || rows[0]?.code || 'en';
}

function toLangMeta(row: {
  code: string;
  name: string;
  nativeName: string;
  rtl: boolean;
  isDefault?: boolean;
}): LangMeta {
  return {
    code: String(row.code).toLowerCase(),
    label: row.name,
    nativeLabel: row.nativeName || row.name,
    dir: (row.rtl || isRtlLanguage(String(row.code))) ? 'rtl' : 'ltr',
    flag: FLAG_BY_CODE[String(row.code).toLowerCase()] || '🌐',
    isDefault: !!row.isDefault,
  };
}

interface I18nContextValue {
  language: string;
  langMeta: LangMeta;
  setLanguage: (lang: string) => void;
  t: (key: string, fallback?: string) => string;
  /** Translate known English UI copy (buttons, fields, headers) via Translator Master catalog. */
  tl: (english: string) => string;
  languages: string[];
  langList: LangMeta[];
  loading: boolean;
  dir: 'ltr' | 'rtl';
  reloadTranslations: () => Promise<void>;
}

const defaultLang = FALLBACK_LANGUAGES[0];

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  langMeta: defaultLang,
  setLanguage: () => undefined,
  t: (key, fallback) => fallback || key,
  tl: (english) => english,
  languages: FALLBACK_LANGUAGES.map((l) => l.code),
  langList: FALLBACK_LANGUAGES,
  loading: false,
  dir: 'ltr',
  reloadTranslations: async () => undefined,
});

function resolveTenantPanelLanguage(rows: LangMeta[]): string {
  return tenantDefaultLanguage(rows);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const reduxSlug = useSelector((s: RootState) => s.tenant.slug);
  const tenantSlug = String(reduxSlug || currentTenantSlug() || '').trim().toLowerCase();
  const [language, setLanguageState] = useState('en');
  const [translations, setTranslations] = useState<TranslationMap>({});
  const [langList, setLangList] = useState<LangMeta[]>(FALLBACK_LANGUAGES);
  const [loading, setLoading] = useState(false);
  const loadSeq = useRef(0);

  const langMeta = langList.find((l) => l.code === language) || langList[0] || defaultLang;
  // Prefer backend RTL flag; always force RTL for Arabic / Hebrew / Farsi / Urdu
  const dir: 'ltr' | 'rtl' = (langMeta.dir === 'rtl' || isRtlLanguage(language)) ? 'rtl' : 'ltr';

  const tenantHeaders = (slug: string) => (
    slug ? { 'X-Tenant-Slug': slug } : {}
  );

  const applyTenantLocale = useCallback(async (slug: string) => {
    const seq = ++loadSeq.current;
    if (!slug) {
      setLangList(FALLBACK_LANGUAGES);
      setLanguageState('en');
      setTranslations({});
      return;
    }
    setLoading(true);
    try {
      const [langRes, defRes] = await Promise.all([
        api.get('/locale/public/languages', {
          headers: tenantHeaders(slug),
          params: { tenant: slug },
        }),
        api.get('/locale/public/defaults', {
          headers: tenantHeaders(slug),
          params: { tenant: slug },
        }).catch(() => null),
      ]);
      if (seq !== loadSeq.current) return;
      const rows = langRes.data.data;
      const mapped = Array.isArray(rows) && rows.length
        ? rows.map(toLangMeta)
        : FALLBACK_LANGUAGES;
      const dbDefault = String(defRes?.data?.data?.defaultLanguage || '').toLowerCase().trim();
      const resolved = mapped.some((r) => r.code === dbDefault)
        ? dbDefault
        : resolveTenantPanelLanguage(mapped);
      setLangList(mapped);
      persistActiveLanguage(resolved, slug, false);
      setLanguageState(resolved);

      const trRes = await api.get(`/locale/public/translations/${resolved}`, {
        headers: tenantHeaders(slug),
        params: { tenant: slug },
      });
      if (seq !== loadSeq.current) return;
      setTranslations((trRes.data.data?.translations || {}) as TranslationMap);
    } catch {
      if (seq !== loadSeq.current) return;
      const resolved = resolveTenantPanelLanguage(FALLBACK_LANGUAGES);
      setLangList(FALLBACK_LANGUAGES);
      persistActiveLanguage(resolved, slug, false);
      setLanguageState(resolved);
      setTranslations({});
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void applyTenantLocale(tenantSlug);
  }, [applyTenantLocale, tenantSlug]);

  useEffect(() => {
    const onLangsChanged = () => {
      void applyTenantLocale(currentTenantSlug() || tenantSlug);
    };
    window.addEventListener('locale-languages-changed', onLangsChanged);
    return () => window.removeEventListener('locale-languages-changed', onLangsChanged);
  }, [applyTenantLocale, tenantSlug]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', dir);
    document.documentElement.style.setProperty('direction', dir);
    document.body.style.setProperty('direction', dir);
  }, [dir, language]);

  const setLanguage = (lang: string) => {
    const code = lang.toLowerCase().trim();
    const slug = tenantSlug || currentTenantSlug();
    persistActiveLanguage(code, slug, true);
    setLanguageState(code);
    const seq = ++loadSeq.current;
    if (!slug) return;
    void (async () => {
      try {
        const res = await api.get(`/locale/public/translations/${code}`, {
          headers: tenantHeaders(slug),
          params: { tenant: slug },
        });
        if (seq !== loadSeq.current) return;
        setTranslations((res.data.data?.translations || {}) as TranslationMap);
      } catch {
        if (seq !== loadSeq.current) return;
        setTranslations({});
      }
    })();
  };

  const reloadTranslations = useCallback(async () => {
    const slug = tenantSlug || currentTenantSlug();
    if (!slug) return;
    const seq = ++loadSeq.current;
    try {
      const res = await api.get(`/locale/public/translations/${language}`, {
        headers: tenantHeaders(slug),
        params: { tenant: slug },
      });
      if (seq !== loadSeq.current) return;
      setTranslations((res.data.data?.translations || {}) as TranslationMap);
    } catch {
      /* keep current catalog */
    }
  }, [language, tenantSlug]);

  const t = useCallback((key: string, fallback?: string) => {
    if (!key) return fallback || '';
    if (translations[key]) return translations[key];
    // Support callers passing bare keys like "nav.dashboard" or "admin.nav.dashboard"
    const shortKey = key.includes('.') ? key.split('.').slice(1).join('.') : key;
    for (const [k, v] of Object.entries(translations)) {
      if (k === key || k.endsWith(`.${key}`) || k.endsWith(`.${shortKey}`) || k === shortKey) return v;
    }
    return fallback || key;
  }, [translations]);

  const tl = useCallback((english: string) => translateUiText(english, t), [t]);

  const value = useMemo(() => ({
    language,
    langMeta,
    setLanguage,
    t,
    tl,
    languages: langList.map((l) => l.code),
    langList,
    loading,
    dir,
    reloadTranslations,
  }), [language, langMeta, t, tl, langList, loading, dir, reloadTranslations]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Backward compatibility for components importing SUPPORTED_LANGUAGES
export const SUPPORTED_LANGUAGES = FALLBACK_LANGUAGES;
